import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import learnedLogo from "@/assets/logo.png";
import { useTranslation } from "react-i18next";

import { ClientLogos } from "@/components/ClientLogos";
import { T, THtml } from "@/components/editable/T";
import { PressMentions } from "@/components/sections/PressMentions";

const PAGE = "aboutUs";

const AboutUs = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language;

  return (
    <>
      {/* ───── HERO ───── */}
      <section className="pt-[132px] pb-10 px-6 relative">
        <div className="container mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-10">
            <div className="animate-fade-in">
              <T
                page={PAGE}
                k="hero.badge"
                d="Over ons"
                as="p"
                className="text-sm font-semibold mb-4 uppercase tracking-wide"
                style={{ color: '#9055EE' }}
              />
              <T
                page={PAGE}
                k="hero.title"
                d="Bouw met ons mee aan eerlijke, betrokken en high-performing teams"
                as="h1"
                className="mb-4"
                style={{ color: '#2A2334', animationDelay: '100ms' }}
              />
              <THtml
                page={PAGE}
                k="hero.body"
                d="Bij Learned geloven wij dat mensen de grootste kracht zijn binnen een organisatie. Groeien zij, dan groeit de organisatie met ze mee. Daarom verdient ieder talent een eerlijke kans om te groeien: aan de hand van duidelijke verwachtingen, onderbouwde evaluaties en eerlijke beloningen."
                as="p"
                className="text-lead text-muted-foreground mb-6 max-w-xl"
              />
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="text-sm px-6 py-5 rounded-full bg-[#9055EE] hover:bg-[#753DCE]"
                  onClick={() =>
                    document.getElementById("ons-verhaal")?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  <T page={PAGE} k="hero.cta1" d="Ons verhaal" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="text-sm px-6 py-5 rounded-full bg-white hover:bg-gray-50"
                  onClick={() => {
                    const careersPath = lang === 'de' ? 'karriere' : lang === 'nl' ? 'werken-bij' : 'careers';
                    navigate(`/${lang}/${careersPath}/`);
                  }}
                >
                  <T page={PAGE} k="hero.cta2" d="Werken bij" />
                  <ArrowRight className="ml-2 h-4 w-4" style={{ color: '#9055EE' }} />
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in" style={{ animationDelay: '200ms' }}>
              {/* Floating badges */}
              <div
                className="absolute -top-3 right-4 z-20 bg-white rounded-2xl shadow-lg px-4 py-2 animate-fade-float-in"
                style={{ animationDelay: '600ms', animationFillMode: 'both' }}
              >
                <span className="text-sm font-bold" style={{ color: '#9055EE' }}>350+</span>
                <T page={PAGE} k="hero.organizationsLabel" d="organisaties" as="span" className="text-xs text-muted-foreground ml-1" />
              </div>
              <div
                className="absolute top-1/2 -translate-y-1/2 -left-2 z-20 bg-white rounded-2xl shadow-lg px-4 py-2.5 flex items-center gap-1.5 animate-fade-float-in"
                style={{ animationDelay: '800ms', animationFillMode: 'both' }}
              >
                {['P', 'A', 'C', 'E'].map((letter) => (
                  <span key={letter} className="text-lg font-bold" style={{ color: '#9055EE' }}>
                    {letter}
                  </span>
                ))}
              </div>
              <div
                className="absolute -bottom-3 right-6 z-20 bg-white rounded-2xl shadow-lg px-4 py-2 flex items-center gap-2 animate-fade-float-in"
                style={{ animationDelay: '1000ms', animationFillMode: 'both' }}
              >
                <span className="text-base">🚀</span>
                <span className="text-xs font-semibold" style={{ color: '#2A2334' }}>Since 2018</span>
              </div>

              {/* Photo */}
              <div className="rounded-3xl overflow-hidden max-w-md mx-auto" style={{ border: '10px solid rgba(255, 255, 255, 0.7)', aspectRatio: '4/3' }}>
                <img
                  src="/images/about/founders-hero.webp"
                  alt="Oprichters Learned - Rick, Paul en Joost Kuijf"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: '50% 40%' }}
                  loading="eager"
                />
              </div>
            </div>
          </div>
          <ClientLogos />
        </div>
      </section>

      {/* ───── MISSION STATEMENT ───── */}
      <section className="py-20 px-6 section-alt">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <span className="block text-7xl md:text-8xl leading-none -mb-4" style={{ color: '#9055EE', fontFamily: 'Georgia, "Times New Roman", serif' }}>&ldquo;</span>
            <THtml
              page={PAGE}
              k="mission.text"
              d="Wij geloven dat betere organisaties beginnen bij een helder fundament: duidelijke verwachtingen, eerlijke kansen om te groeien, en een passende beloning voor wat je laat zien. Daarom bouwen we aan een manier van werken waarin ontwikkeling, evaluatie en beloning niet los van elkaar staan, maar elkaar juist versterken."
              as="p"
              className="text-2xl md:text-3xl font-semibold leading-relaxed"
              style={{ color: '#2A2334' }}
            />
          </div>
        </div>
      </section>

      {/* ───── ONS VERHAAL ───── */}
      <section id="ons-verhaal" className="py-20 px-6">
        <div className="container mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <T page={PAGE} k="story.badge" d="Ons verhaal" as="p" className="text-sm text-muted-foreground mb-4 uppercase tracking-wide" />
            <h2 className="feature-section-heading mb-4 md:max-w-[70%] md:mx-auto text-foreground">
              <T page={PAGE} k="story.title" d="Van idee naar 350+ organisaties" />
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="animate-fade-in">
              <THtml
                page={PAGE}
                k="story.body1"
                d="Toen onze oprichters nog in loondienst waren, zagen ze dat bijna elke afdeling datagedreven werkte – behalve HR. Belangrijke beslissingen over evaluaties, doorgroei en salaris werden vaak op onderbuikgevoel genomen. Tijd dus voor eerlijke en objectieve evaluaties. Omdat de juiste oplossing ontbrak, startten de drie broers Rick, Paul en Joost Kuijf in 2018 Learned: software voor objectief Performance Management."
                as="p"
                className="text-base text-muted-foreground mb-6"
              />
              <THtml
                page={PAGE}
                k="story.body2"
                d="Vandaag gebruiken meer dan 350 organisaties onze software om prestaties te verbeteren, betrokkenheid te vergroten en verloop te verminderen. Dat is hard nodig, want 4 op de 5 werknemers ervaren evaluaties als oneerlijk (Gallup, 2024), 86% voelt zich niet gestimuleerd om te groeien en 75% van HR-leiders ziet dat managers worstelen door werkdruk en gebrek aan opleiding. Learned helpt organisaties juist hier wendbaarder en toekomstbestendig te worden."
                as="p"
                className="text-base text-muted-foreground"
              />
            </div>
            <div className="animate-fade-in" style={{ animationDelay: '200ms' }}>
              <div className="aspect-square rounded-3xl overflow-hidden max-w-md mx-auto" style={{ border: '10px solid rgba(255, 255, 255, 0.7)' }}>
                <img
                  src="/images/about/founders.webp"
                  alt="Oprichters Learned - Rick, Paul en Joost Kuijf"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── PACE WAARDEN ───── */}
      <section className="py-20 px-6 section-alt">
        <div className="container mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <T page={PAGE} k="values.badge" d="Onze kernwaarden" as="p" className="text-sm text-muted-foreground mb-4 uppercase tracking-wide" />
            <h2 className="feature-section-heading mb-4 text-foreground">
              <T page={PAGE} k="values.title" d="Wat ons team kenmerkt" />
            </h2>
          </div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { letter: 'P', key: 'personal_growth', titleLine1: 'Personal', titleLine2: 'growth', descDefault: 'We geloven in continue feedback en persoonlijke ontwikkeling.' },
              { letter: 'A', key: 'adaptive_thinking', titleLine1: 'Adaptive', titleLine2: 'thinking', descDefault: 'We staan open voor verandering en verbeteren continu.' },
              { letter: 'C', key: 'contribute', titleLine1: 'Contribute to', titleLine2: 'the BHAG', descDefault: 'We werken samen aan onze missie om impact te maken.' },
              { letter: 'E', key: 'effective', titleLine1: 'Effective', titleLine2: 'collaboration', descDefault: 'We verbinden ons met elkaar en bouwen op elkaars kracht.' },
            ].map((v, index) => (
              <div
                key={v.key}
                className="rounded-2xl border border-border bg-background p-8 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <span className="inline-block text-4xl font-bold mb-4 bg-gradient-to-br from-[#9055EE] to-[#B88AFE] bg-clip-text text-transparent">
                  {v.letter}
                </span>
                <h4
                  className="mb-4 text-xl font-semibold leading-tight min-h-[3.5rem]"
                  style={{ color: '#2A2334' }}
                >
                  {v.titleLine1}<br />{v.titleLine2}
                </h4>
                <T
                  page={PAGE}
                  k={`values.pace.${v.key}.description`}
                  d={v.descDefault}
                  as="p"
                  className="text-muted-foreground"
                />
              </div>
            ))}
          </div>
        </div>
      </section>


      <PressMentions />

      {/* ───── CTA: Werken bij ───── */}
      <section className="py-20 px-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="animate-fade-in">
              <h2 className="feature-section-heading mb-4 text-foreground">
                <T page={PAGE} k="cta.title" d="Wil je bijdragen aan onze missie?" />
              </h2>
              <THtml
                page={PAGE}
                k="cta.body"
                d="Werk ook mee aan onze missie voor een arbeidsmarkt waarin iedereen eerlijk beoordeeld wordt!"
                as="p"
                className="text-lead text-muted-foreground mb-6"
              />
              <Button
                size="lg"
                className="text-sm px-6 py-5 rounded-full bg-[#9055EE] hover:bg-[#753DCE]"
                onClick={() => {
                  const careersPath = lang === 'de' ? 'karriere' : lang === 'nl' ? 'werken-bij' : 'careers';
                  navigate(`/${lang}/${careersPath}/`);
                }}
              >
                <T page={PAGE} k="cta.button" d="Bekijk vacatures" />
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="animate-fade-in" style={{ animationDelay: '200ms' }}>
              <div className="overflow-hidden shadow-lg" style={{ borderRadius: '9999px', aspectRatio: '16/9' }}>
                <img
                  src="/images/about/team-cta.webp"
                  alt="Werken bij Learned"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      
    </>
  );
};

export default AboutUs;
